// ------------- import external dependencies ------------
import React, { useState } from "react";
import { AiFillEye, AiFillEyeInvisible } from "react-icons/ai";
import __ from "lodash";

// ------------- import internal dependencies -------------
import {
  FormInputWrapper,
  Input,
  Label,
  Star,
  ErrorMessage,
  LimitReachedMessage,
  PasswordVisibility,
  LabelWrapper,
} from "./input.style";
import { Tag } from "../../StyledTable/StyledTable.styles";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import Checkbox from "../../Checkbox/Checkbox";

export interface FormProps {
  label?: string;
  name?: string;
  dataId?: string;
  value?: any;
  placeholder?: string;
  onChange?: (e: any) => void;
  onBlur?: (e?: any) => void;
  // errorMessage?: string | string[] | null;
  errorMessage?: any; // refine type
  setFieldValue?: (fieldName: string, value: any) => void;
  type?: React.HTMLInputTypeAttribute;
  isImportant?: boolean;
  variant?: string;
  children?: React.ReactNode;
  showToggleBox?: boolean;
  toggleBoxName?: string;
  toggleBoxLabel?: string;
  toggleBoxValue?: boolean;
  tooltipContent?: string;
  onCheck?: any;
  disabled?: boolean;
  addRightMargin?: boolean;
  addWidth?: boolean;
  showBadges?: boolean;
  qapi?: boolean;
  constraint?: any;
  incidentDateTime?: string;
  incidentCreationDate?: string;
  // Max characters allowed, derived from the Salesforce field length so users
  // can't submit a value the backend would reject with STRING_TOO_LONG. Flows
  // onto the native <Input> via the inputProps spread below.
  maxLength?: number;
}

const FormInput = (props: FormProps) => {
  const {
    label,
    errorMessage,
    type,
    name,
    isImportant,
    variant = "md",
    children,
    onChange,
    onBlur,
    showToggleBox,
    toggleBoxName,
    toggleBoxLabel,
    toggleBoxValue,
    tooltipContent,
    setFieldValue,
    onCheck,
    disabled,
    showBadges,
    qapi,
    value,
    maxLength,
  } = props;

  const [showPassword, setShowPassword] = useState(
    type === "password" ? false : true
  );

  // Surface a red hint once the value reaches its Salesforce-backed maxLength so
  // the user understands the input stopped accepting characters (MARCC-690). The
  // maxLength attribute hard-stops typing, so length never exceeds the cap.
  const isLimitReached =
    typeof maxLength === "number" &&
    typeof value === "string" &&
    value.length >= maxLength;
  const inputProps = __.omit(props, ["errorMessage", "isImportant"]);
  function handleCheck(e: any) {
    if (toggleBoxName && name && setFieldValue) {
      setFieldValue(toggleBoxName, !toggleBoxValue);
      if (onCheck) onCheck(e);
    }
  }

  return (
    <FormInputWrapper>
      <LabelWrapper>
        <Label htmlFor={label} data-testid={`label-${name}`}>
          {label} <Star>{isImportant ? "*" : ""}</Star>{" "}
          {!!showBadges && qapi && (
            <span className="float-right ml-1 inline flex">
              <Tag>Q</Tag>
            </span>
          )}
          {tooltipContent && (
            <Tooltip anchorSelect={`.anchor-element-${props.name}`} place="top">
              {" "}
              {tooltipContent}{" "}
            </Tooltip>
          )}
        </Label>
        {showToggleBox && (
          <Checkbox
            name={toggleBoxName || ""}
            label={toggleBoxLabel || ""}
            checked={toggleBoxValue}
            onChange={handleCheck}
            onBlur={onBlur}
            disabled={disabled}
          />
        )}
      </LabelWrapper>
      <div
        className={`anchor-element-${props.name}`}
        data-testid={`anchor-element-${props.name}`}
      >
        {children ? (
          children
        ) : (
          <Input
            data-testid={`input-${name}`}
            // data-testid={`${name}`}
            {...inputProps}
            type={showPassword ? "text" : "password"}
            variant={variant}
            onChange={onChange}
            disabled={disabled}
          />
        )}
        {type === "password" && (
          <PasswordVisibility>
            {showPassword ? (
              <AiFillEyeInvisible
                onClick={() => setShowPassword(false)}
                color="#B8BCCA"
                size={23}
              />
            ) : (
              <AiFillEye
                onClick={() => setShowPassword(true)}
                color="#B8BCCA"
                size={23}
              />
            )}
          </PasswordVisibility>
        )}
      </div>
      {/* </InputWrapper> */}
      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
      {isLimitReached && (
        <LimitReachedMessage data-testid={`limit-reached-${name}`}>
          You&apos;ve reached the {maxLength} character limit.
        </LimitReachedMessage>
      )}
    </FormInputWrapper>
  );
};

export default FormInput;
